import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { characterAutoCompletePluginKey } from './CharacterAutoComplete';

// Enter key: what to create after current node (when it has content)
const ENTER_NEXT: Record<string, string> = {
  characterName: 'dialogue',
  dialogue: 'characterName',
  parenthetical: 'dialogue',
  stageDirection: 'characterName',
  actHeading: 'sceneHeading',
  sceneHeading: 'stageDirection',
  paragraph: 'characterName',
};

// Enter key: what to convert to when current node is empty
const ENTER_EMPTY_CONVERT: Record<string, string> = {
  characterName: 'stageDirection',
  dialogue: 'characterName',
  parenthetical: 'dialogue',
  stageDirection: 'characterName',
  paragraph: 'characterName',
};

// Tab key: forward cycle
const TAB_NEXT: Record<string, string> = {
  dialogue: 'parenthetical',
  parenthetical: 'stageDirection',
  stageDirection: 'dialogue',
  paragraph: 'dialogue',
};

// Shift+Tab: reverse cycle
const SHIFT_TAB_NEXT: Record<string, string> = {
  dialogue: 'stageDirection',
  parenthetical: 'dialogue',
  stageDirection: 'parenthetical',
  paragraph: 'stageDirection',
};

export const playwritingKeymapPluginKey = new PluginKey('playwritingKeymap');

export const PlaywritingKeymap = Extension.create({
  name: 'playwritingKeymap',

  addProseMirrorPlugins() {
    const { editor } = this;

    return [
      new Plugin({
        key: playwritingKeymapPluginKey,
        props: {
          handleKeyDown(view, event) {
            // Defer to autocomplete when it's showing suggestions
            const acState = characterAutoCompletePluginKey.getState(view.state);
            if (acState?.active) return false;

            const { $from } = view.state.selection;
            const node = $from.parent;
            const nodeType = node?.type?.name ?? null;

            if (!nodeType) return false;

            // --- ENTER ---
            if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
              const isEmpty = node.content.size === 0;

              // Empty node: convert in place
              if (isEmpty && ENTER_EMPTY_CONVERT[nodeType]) {
                event.preventDefault();
                editor.commands.setNode(ENTER_EMPTY_CONVERT[nodeType]);
                return true;
              }

              // Node with content: split and create next type
              const nextType = ENTER_NEXT[nodeType];
              if (nextType) {
                event.preventDefault();
                editor.chain().splitBlock().setNode(nextType).run();
                return true;
              }

              return false;
            }

            // --- TAB ---
            if (event.key === 'Tab' && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
              event.preventDefault();
              const nextType = TAB_NEXT[nodeType];
              if (nextType) {
                editor.commands.setNode(nextType);
              }
              return true; // always consume Tab
            }

            // --- SHIFT+TAB ---
            if (event.key === 'Tab' && event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
              event.preventDefault();
              const nextType = SHIFT_TAB_NEXT[nodeType];
              if (nextType) {
                editor.commands.setNode(nextType);
              }
              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});
